import { logMessageToSentry } from 'venn-utils';
import type { IHelpCenterLinks } from '../../getTextThemeProvider';
import { getHelpCenterLinks } from './index';

/**
 * Backend content ships `{HELP_*}` tokens instead of hard coded help center urls so that the link
 * resolves against whichever help center the current theme and venn_help_on_riskpro_ff select.
 */
const HELP_PLACEHOLDER_LINKS: Record<string, keyof IHelpCenterLinks> = {
  HELP_FACTOR_PERIODS: 'FACTOR_PERIODS_SOURCES_HREF',
  HELP_DISCLOSURE_AND_DISCLAIMER: 'DISCLOSURE_AND_DISCLAIMER_HREF',
};

const PLACEHOLDER = /\{(HELP_[A-Z0-9_]+)\}/g;
const UNRESOLVED_MARKDOWN_LINK = /\[([^\]]+)\]\(\{HELP_[A-Z0-9_]+\}\)/g;

/** Content is re-resolved on every render, so each unknown name is reported once per page load. */
const reportedUnknownPlaceholders = new Set<string>();

const reportUnknownPlaceholder = (name: string) => {
  if (reportedUnknownPlaceholders.has(name)) {
    return;
  }
  reportedUnknownPlaceholders.add(name);
  logMessageToSentry(`Unknown help center placeholder '${name}'.`);
};

/**
 * Resolved per call rather than cached: refreshHelpCenterLinks re-resolves the map once the feature
 * flags load, and callers render before and after that point.
 */
export const resolveHelpCenterPlaceholders = (content: string): string => {
  const links = getHelpCenterLinks();

  return content
    .replace(PLACEHOLDER, (token, name: string) => {
      const key = HELP_PLACEHOLDER_LINKS[name];
      if (!key) {
        // An empty link is an article the current help center lacks, but an unmapped name is a typo in backend content
        reportUnknownPlaceholder(name);
        return token;
      }
      return links[key] || token;
    })
    .replace(UNRESOLVED_MARKDOWN_LINK, '$1')
    .replace(PLACEHOLDER, '');
};
